/**
 * What you see before there is a page.
 *
 * A blank canvas gives no clue what to do first, and the answer is not obvious in a builder
 * where a Section is the only thing that can sit at the top level. So this says it plainly
 * and offers the two ways in: start from a preset, or start from a Section.
 */

import { Layers, MousePointerClick, Sparkles } from 'lucide-react';

export function EmptyState(): React.ReactElement {
  return (
    <div className="lp-empty">
      <div className="lp-empty__card">
        <span className="lp-empty__mark" aria-hidden>
          <Sparkles size={26} strokeWidth={2} />
        </span>

        <h2 className="lp-empty__title">Let’s build a page</h2>
        <p className="lp-empty__lead">
          Drag a block from the left to get going. Everything on a page lives inside a
          <strong> Section</strong>.
        </p>

        <ol className="lp-empty__steps">
          <li>
            <span className="lp-empty__num">1</span>
            <span>
              <strong>Start with a Section</strong> — a full-width band. Set its background and
              spacing once, and everything inside inherits the page’s measure.
            </span>
          </li>
          <li>
            <span className="lp-empty__num">2</span>
            <span>
              <strong>Add a Heading</strong>, then a <strong>Columns</strong> split beneath it.
              Each column holds as many elements as you like — image, heading, body, button.
            </span>
          </li>
          <li>
            <span className="lp-empty__num">3</span>
            <span>
              <strong>Copy to Webflow</strong> when you’re done, one section at a time.
            </span>
          </li>
        </ol>

        <div className="lp-empty__tips">
          <p className="lp-empty__tip">
            <Sparkles size={15} strokeWidth={2} aria-hidden />
            <span>
              In a hurry? Open <strong>Presets</strong> in the block panel for a ready-made hero
              or feature grid — they’re ordinary blocks underneath, so take them apart.
            </span>
          </p>
          <p className="lp-empty__tip">
            <Layers size={15} strokeWidth={2} aria-hidden />
            <span>
              Use the <strong>Outline</strong> tab to see the structure and drag things between
              columns.
            </span>
          </p>
          <p className="lp-empty__tip">
            <MousePointerClick size={15} strokeWidth={2} aria-hidden />
            <span>
              The icons along the top switch between desktop, tablet and phone — they’re
              Webflow’s real breakpoints, so what you see is what you’ll get.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
